import { useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconChevronRight,
  IconError,
  IconHourglass,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { runDuplicateCheck } from "../api";
import { useOfflineQueue, useOnlineStatus } from "../hooks";
import { enqueue, getLocalLead, listQueue, upsertLocalLead } from "../store";
import type { DuplicateCandidate } from "../types";

/**
 * S2 — Duplicate check results (handoff TASK-002, s2-duplicate-check-* boards).
 * Calls POST /leads/:id/duplicate-check on load; candidates with evidence
 * badges; empty state → Continue to KYC (S4); loading / error / offline-queued
 * variants. Local (unsynced) leads always render the offline-queued variant.
 */

export function candidateKey(c: DuplicateCandidate): string {
  return `${c.type}-${c.id}`;
}

export function S2DuplicateCheck() {
  const { id = "" } = useParams();
  const online = useOnlineStatus();
  const isLocal = id.startsWith("local-");
  const localRecord = isLocal ? getLocalLead(id) : undefined;

  // Local draft that has since synced → jump to the server-id route.
  if (isLocal && localRecord?.serverId !== undefined) {
    return <Navigate to={`/field/leads/${localRecord.serverId}/duplicates`} replace />;
  }

  return isLocal || !online ? (
    <OfflineQueued leadRef={id} />
  ) : (
    <OnlineCheck leadId={Number(id)} />
  );
}

/** Offline variant: "Check queued for sync" (s2-duplicate-check-offline-variant). */
function OfflineQueued({ leadRef }: { leadRef: string }) {
  useFieldHeader("Duplicate check", "Queued");
  useFieldJourney({ flow: "leads", step: 1, backTo: "/field/leads" });
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const { refresh: refreshQueue } = useOfflineQueue(online);

  // Enqueue the duplicate check once so it runs automatically on reconnect.
  useEffect(() => {
    const queued = listQueue().some((q) => q.kind === "duplicate-check" && q.leadRef === leadRef);
    if (!queued) {
      enqueue({
        queueId: crypto.randomUUID(),
        kind: "duplicate-check",
        leadRef,
        createdAt: new Date().toISOString(),
      });
      const local = getLocalLead(leadRef);
      if (local) {
        upsertLocalLead({
          ...local,
          status: local.serverId ? "pending-duplicate-check" : "pending-sync",
        });
      }
      refreshQueue();
    }
  }, [leadRef, refreshQueue]);

  return (
    <div className="pg-state">
      <span className="state-icon info">
        <IconHourglass size={40} />
      </span>
      <h2>Check queued for sync</h2>
      <p>
        Duplicate check will run automatically when back online.
        <br />
        Lead saved as "pending duplicate check".
      </p>
      <AppButton variant="secondary" type="button" onClick={() => navigate("/field/leads/new")}>
        Continue capturing offline
      </AppButton>
    </div>
  );
}

function OnlineCheck({ leadId }: { leadId: number }) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["duplicate-check", leadId],
    queryFn: () => runDuplicateCheck(leadId),
    retry: 1,
    staleTime: 60_000,
  });

  const count = query.data?.candidate_count;
  useFieldHeader(
    "Duplicate check",
    query.isPending
      ? "Searching…"
      : query.isError
        ? "Search failed"
        : count === 0
          ? "Search complete"
          : `${count} possible match${count === 1 ? "" : "es"} found`,
  );
  useFieldJourney({ flow: "leads", step: 1, backTo: "/field/leads" });

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Checking for duplicates…</h2>
        <p>Name, phone, NIDA and GPS checked against existing records</p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Duplicate check failed</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  const data = query.data;

  if (data.candidate_count === 0) {
    return (
      <div className="pg-state">
        <span className="state-icon success">
          <IconCheck size={40} />
        </span>
        <h2>No duplicates found</h2>
        <p>Name, phone, NIDA and GPS checked against existing records. Continue to submit KYC for this new customer.</p>
        <AppButton type="button" onClick={() => navigate(`/field/leads/${leadId}/kyc`)}>
          Continue to KYC
        </AppButton>
        <AppButton variant="secondary" type="button" onClick={() => navigate("/field/leads")}>
          Back to Leads
        </AppButton>
      </div>
    );
  }

  return (
    <>
      <p className="pg-text-muted" style={{ margin: 0, fontSize: "var(--pg-fs-meta)" }}>
        Review each match below. If none apply, continue as a new customer to submit KYC.
      </p>
      {data.candidates.map((c) => (
        <div className="pg-candidate" key={candidateKey(c)}>
          <div className="cand-head">
            <span className="cand-name">{c.name}</span>
            {c.type === "customer" ? (
              <span className="pg-badge warning">
                <IconWarning size={12} /> possible
              </span>
            ) : (
              <span className="pg-badge info">
                <IconHourglass size={12} /> possible
              </span>
            )}
          </div>
          <span className="cand-evidence">{c.evidence.join(" · ")}</span>
          <Link className="cand-review" to={`/field/leads/${leadId}/duplicates/${candidateKey(c)}`}>
            Review <IconChevronRight size={14} />
          </Link>
        </div>
      ))}

      <div className="pg-actions">
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate(`/field/leads/${leadId}/kyc`)}
        >
          Continue as new customer
        </AppButton>
        <p style={{ margin: 0, fontSize: "var(--pg-fs-meta)", color: "var(--pg-text-muted)", textAlign: "center" }}>
          Skips merge/reject — opens KYC for this lead as a new registration
        </p>
        <AppButton variant="secondary" type="button" onClick={() => navigate("/field/leads")}>
          Back to Leads
        </AppButton>
      </div>
    </>
  );
}
